import { useRequest } from "../../../hooks/useRequest";
import { useState } from "react";
import useHttpResponseAlert from "../../../hooks/useHttpResponseAlert";
import Utils from "../utils";
import routes from "../../../routes";
import type { EditSolutionFormValues } from "../edit/form/types";
import type { SolutionUpdatePayload } from "../types";

export function useUpdateSolutionDetails(id: string) {
  const [isEditing, setEditing] = useState(false);

  const { fetch, response, error, loading } = useRequest();

  const { alert, setAlert } = useHttpResponseAlert(
    error,
    response,
    "Project has been updated successfully!",
  );

  const sendUpdate = async (payload: SolutionUpdatePayload) => {
    const geojsonFile = payload.riskMonitoring?.geospatialFile;
    const url = routes.api.solution.individual({ id });

    let result;
    if (!(geojsonFile instanceof File)) {
      result = await fetch("PATCH", url, payload);
    } else {
      const multipartForm = new FormData();
      const solutionMetadata = {
        ...payload,
        riskMonitoring: {
          ...payload.riskMonitoring,
          geospatialFile: null,
        },
      };
      multipartForm.set("payload", JSON.stringify(solutionMetadata));
      multipartForm.set("file", geojsonFile, geojsonFile.name);
      result = await fetch("PATCH", url, multipartForm, undefined, false);
    }

    if (result?.status === 204) setEditing(false);

    return result;
  };

  const handleSubmit = async (form: EditSolutionFormValues) => {
    const updatePayload = Utils.SolutionFormBuilder.updateSolution(form);
    const existingOutcomesV2 = form["outcomes-v2"] || [];
    const outcomesV3 = updatePayload.outcomes || [];
    const { outcomes: _outcomes, ...payloadWithoutOutcomes } = updatePayload;
    const normalisedPayload: SolutionUpdatePayload = {
      ...payloadWithoutOutcomes,
      outcomes: {
        v2: outcomesV3.length > 0 ? outcomesV3 : existingOutcomesV2,
        v3: outcomesV3,
      },
    };

    return sendUpdate(normalisedPayload);
  };

  return {
    loading,
    isEditing,
    setEditing,
    update: handleSubmit,
    updateSection: sendUpdate,
    alert,
    setAlert,
  };
}
